import React, { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router";
import { useSelector } from "react-redux";
import { Button, Form, Input, Radio, Select } from "antd";
import UploadWidget from "./UploadWidget";
import UploadThumbnail from "../UploadThumbnail";
import UploadScreenshots from "./UploadScreenshots";
import { Http } from "../../../helpers/axois";
import { ReactComponent as LeftIcon } from "../../../assets/svg/layer-left.svg";
import "./styles/create-widget.scss";
import { logger } from "../../../constant/winston";
import { hasValue } from "../../../constant/utils";
import { useToast } from "../../../helpers/commonFunction";
import { AppState } from "../../../stateManager/store";

interface IWidget {
  name: string;
  buildId: string;
  description: string;
  shortDescription: string;
  screenshots: string[];
  currentVersion: string;
  isPaid: boolean;
  tags: string[];
  objectUrl: string;
  isPublished: boolean;
  previewImage: string;
  supportEmail: string;
  authorWebsite: string;
  [key: string]: string | boolean | string[];
}

const requiredFields = [
  "name",
  "buildId",
  "description",
  "shortDescription",
  "objectUrl",
  "currentVersion"
]

interface ICreateWidget {
  setWidgets?: any;
}

const CreateWidget: React.FC<ICreateWidget> = (props) => {
  const history = useHistory();
  const pathname = history.location.pathname;
  const productId = pathname.split('/')[4];
  const path = pathname.split('/')[3]
  const orgId = useSelector((state: any) => state.org.selectedOrg.id);
  const firstName = useSelector((state: any) => state.user.userDetails.firstName);
  const [editable, setEditable] = useState<boolean>(false)
  const [widget, setWidget] = useState<IWidget>({
    name: "",
    buildId: "",
    description: "",
    shortDescription: "",
    screenshots: [],
    currentVersion: "",
    isPaid: false,
    tags: [],
    objectUrl: "",
    isPublished: true,
    supportEmail: "",
    authorWebsite: "",
    previewImage: "",
  });
  const { selectedOrg } = useSelector((state: AppState) => state.org)
  const loggedInUserId = useSelector((state: any) => {
      return state.user.data.user.id
  })
  const { ErrorToast, SuccessToast } = useToast()
  const orgPermissions: any = useSelector((state: any) => {
    return state.org.orgPermissions
  }, (oldPermissions: any, newPermissions: any) => JSON.stringify(oldPermissions) === JSON.stringify(newPermissions))
  const handleSubmit = async (e: any) => {
    e.preventDefault();

    const data = {
      ...widget,
      type: "widget"
    }

    if (path === "createWidget") {
      const endpoint = `/orgs/${orgId}/store/products`;

      // did the user filled out all the required fields?
      for (let field of requiredFields) {
        if(!widget[field]) {
          return ErrorToast("Please fill out all required fields")
        }
      }

      // does description and shortDescription satisfies the character count?
      const { description, shortDescription } = widget;
      if(description.length < 100 || description.length > 500) {
        return ErrorToast("Description should be between 100 and 500 characters")
      }
      if(shortDescription.length < 50 || shortDescription.length > 200) {
        return ErrorToast("Short description should be between 50 and 200 characters")
      }

      try {
        const res: any = await Http.post(endpoint, data).toPromise();
        const product: any = {
          ...res.product,
          version_tag: widget.currentVersion,
          first_name: firstName,
        }
        props.setWidgets((prevState: any[]) => [product, ...prevState])
        SuccessToast("Widget created")
        history.goBack()
      } catch (e:any) {
        ErrorToast(`Failed to create new widget: ${e.message}`)
        logger.error(`failed to create new widget: ${e.message}`)
      }
    } else {
      const endpoint = `/orgs/${orgId}/store/products/${productId}`;
      const { objectUrl, buildId, currentVersion, ...payload } = data;
      Http.put(endpoint, payload).subscribe((_res) => {
        SuccessToast("Widget updated")
        history.goBack()
      }, (e) => {
        ErrorToast("Failed to update new widget")
        logger.error(`Failed to update  new widget: ${e.message}`);
      })
    }
  };

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;
    setWidget((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  // unneccessarily complicated in the name of dry code
  const handleSetState = (key: string, value: any, isArray = false) => {
    setWidget((prevState: any) => {
      let updatedValue = prevState[key];

      if (isArray) {
        updatedValue = [...updatedValue, value];
      }

      return {
        ...prevState,
        [key]: isArray ? updatedValue : value,
      };
    });
  };

  const getProduct = useCallback((): void => {
    if ((!orgPermissions?.includes("marketplace.PublishWidget") || !orgPermissions.includes('org.View'))&&  loggedInUserId!=selectedOrg?.owner) {
      ErrorToast("Not Authorized: Error fetching marketplace widgets")
      return
    } 
    Http.get(
      `/orgs/${orgId}/store/products/${productId}`
    ).subscribe((res: any) => {
      const { name, build_id, description, short_description, screenshots, version_tag, tags, object_url, is_paid, is_published, support_email, author_website, preview_image }: any = res.product
      if (res) {
        setWidget({
          name: name,
          buildId: build_id,
          description: description,
          shortDescription: short_description,
          screenshots: screenshots,
          currentVersion: version_tag,
          isPaid: is_paid,
          tags: tags,
          objectUrl: object_url,
          isPublished: is_published,
          supportEmail: support_email,
          authorWebsite: author_website,
          previewImage: preview_image,
        });
      }
    }, (e) => {
      if (e === "Forbidden") {
        ErrorToast("Not Authorized: Error fetching marketplace widgets")
      }
      logger.error(e);
    });
  }, [orgId, productId])

  useEffect(() => {
    if (path === "edit") {
      getProduct()
      setEditable(true)
    }
  }, [getProduct, path])

  const { name, buildId, description, shortDescription, currentVersion, isPaid, screenshots, tags , supportEmail, authorWebsite } = widget
  const SHORT_DESCRIPTION = "Remaining characters 0"

  return (
    <div className="create-widget">
      <div className="create-widget__header">
        <div className="create-widget__header-title">
          <LeftIcon data-test-id="create_widget" onClick={() => history.goBack()} />
          <p>{editable ? "Edit Widget" : "Create Widget" }</p>
        </div>

        <div className="create-widget__actions">
          <Button data-test-id="save" shape="round" className="blue">
            Save
          </Button>
          <Button  data-test-id="submit" shape="round" className="green" onClick={handleSubmit}>
            {editable ? "Update" : "Submit"}
          </Button>
        </div>
      </div>

      <Form className="create-widget__form" onSubmit={handleSubmit}>
        <div className="util-flex">
          <Form.Item label="Widget Title" className="col-md-6" required>
            <Input
              data-test-id="widget_name"
              name="name"
              placeholder="Enter title"
              value={name}
              onChange={handleInputChange}
            />
          </Form.Item>

          <Form.Item
            label="Build Id"
            className="col-md-6"
            required
          >
            <Input
              data-test-id="build_id"
              name="buildId"
              placeholder="Enter build id"
              value={buildId}
              onChange={handleInputChange}
              disabled={editable}
            />
          </Form.Item>
        </div>
        <div className="util-flex">
          <Form.Item label="Description" className="col-md-6" required>
            <Input.TextArea
              data-test-id="description"
              name="description"
              placeholder="Enter description"
              autoSize={{ minRows: 4 }}
              onChange={handleInputChange}
              value={description}
            />
          </Form.Item>

          <Form.Item label="Short Description" className="col-md-6" required>
            <Input.TextArea
              data-test-id="short_description"
              name="shortDescription"
              placeholder="Enter short description"
              autoSize={{ minRows: 3 }}
              onChange={handleInputChange}
              value={shortDescription}
              maxLength={200}
            />
            {hasValue(shortDescription) &&
              <p className="short-description">
                {shortDescription.length === 200 ? SHORT_DESCRIPTION : `Remaining characters ${200 - shortDescription.length}`}
              </p>
            }
          </Form.Item>
        </div>
        <UploadScreenshots setScreenshots={handleSetState} orgId={orgId} screenshots={screenshots && screenshots.length ? screenshots : []} setWidget={setWidget} />

        <UploadThumbnail setThumbnail={handleSetState} orgId={orgId} info={widget} setInfo={setWidget} />

        <Form.Item label="Widget Type">
          <Radio.Group
            data-test-id="widget_ispaid"
            className="col-md-6"
            name="isPaid"
            onChange={(e: any) => handleSetState("isPaid", e.target.value === "paid")}
            value={isPaid ? "paid" : "free"}
          >
            <Radio data-test-id="free" value="free">Free</Radio>
            <Radio data-test-id="paid" value="paid" disabled>Paid</Radio>
          </Radio.Group>
        </Form.Item>

        <UploadWidget setObjectUrl={handleSetState} orgId={orgId} widget={widget} editable={editable} />

        <div className="util-flex">
          <Form.Item label="Tags" className="col-md-6">
            <Select
              data-test-id="tags"
              value={tags}
              mode="tags"
              onChange={(e: string[]) => handleSetState("tags", e)}
            />
          </Form.Item>

          <Form.Item label="Version" className="col-md-6" required>
            <Input
              data-test-id="current_version"
              value={currentVersion}
              name="currentVersion"
              placeholder="Enter version"
              onChange={handleInputChange}
              disabled={editable}
            />
          </Form.Item>
        </div>

        <Form.Item label="Release Notes" className="col-md-12">
          <Input.TextArea
            data-test-id="release_notes"
            autoSize={{ minRows: 5 }}
            name="releaseNotes"
            disabled={editable}
          />
        </Form.Item>

        <div className="util-flex">
          <Form.Item label="Email Address" className="col-md-6">
            <Input
              data-test-id="support_email"
              placeholder="Enter email address"
              name="supportEmail"
              onChange={handleInputChange}
              value={supportEmail}
            />
          </Form.Item>

          <Form.Item label="Website" className="col-md-6">
            <Input
              data-test-id="author_website"
              placeholder="Enter website"
              name="authorWebsite"
              onChange={handleInputChange}
              value={authorWebsite}
            />
          </Form.Item>
        </div>
      </Form>
    </div>
  );
};

export default CreateWidget;
